/**
 * Consentimientos API Service
 * CRUD operations for informed consent management
 *
 * Flujo: descargar plantilla .docx → imprimir → firma física → escanear → subir PDF/imagen
 */

import axios from "axios";
import { api } from "@/lib/api";

const CONSENTIMIENTOS_URL = "/consentimientos/";

// =============================================================================
// TYPES
// =============================================================================

export type TipoConsentimiento =
  | "PANEL_DROGAS"
  | "HIV"
  | "ANTIGENO_SARS_COV2"
  | "COVID19_ANTICUERPOS"
  | "PCR_RT_COVID"
  | "ENTREVISTA_DONANTE";

export type RelacionFirmante =
  | "PACIENTE"
  | "TUTOR"
  | "REPRESENTANTE_LEGAL"
  | "PADRE_MADRE"
  | "OTRO";

export interface Consentimiento {
  id: number;
  paciente: number;
  paciente_nombre: string;
  tipo: TipoConsentimiento;
  tipo_display: string;
  documento_escaneado: string;
  firmante: string;
  relacion_firmante: RelacionFirmante;
  relacion_firmante_display: string;
  documento_firmante: string;
  aceptado: boolean;
  fecha_firma: string;
  observaciones: string;
  tiene_firma: boolean;
  firmado_por_tercero: boolean;
  documento_url: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ConsentimientoListItem {
  id: number;
  tipo: TipoConsentimiento;
  tipo_display: string;
  firmante: string;
  aceptado: boolean;
  fecha_firma: string;
  tiene_firma: boolean;
}

export interface ConsentimientoCreateData {
  paciente: number;
  tipo: TipoConsentimiento;
  documento_escaneado: File;
  firmante: string;
  relacion_firmante: RelacionFirmante;
  documento_firmante?: string;
  aceptado: boolean;
  observaciones?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ConsentimientoFilters {
  paciente?: number;
  tipo?: TipoConsentimiento;
  aceptado?: boolean;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const TIPOS_CONSENTIMIENTO_VALUES: TipoConsentimiento[] = [
  "PANEL_DROGAS",
  "HIV",
  "ANTIGENO_SARS_COV2",
  "COVID19_ANTICUERPOS",
  "PCR_RT_COVID",
  "ENTREVISTA_DONANTE",
];

export const RELACIONES_FIRMANTE_VALUES: RelacionFirmante[] = [
  "PACIENTE",
  "TUTOR",
  "REPRESENTANTE_LEGAL",
  "PADRE_MADRE",
  "OTRO",
];

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get paginated list of consentimientos
 */
export async function getConsentimientos(
  params: ConsentimientoFilters = {}
): Promise<PaginatedResponse<ConsentimientoListItem>> {
  const searchParams = new URLSearchParams();

  if (params.paciente) searchParams.append("paciente", String(params.paciente));
  if (params.tipo) searchParams.append("tipo", params.tipo);
  if (params.aceptado !== undefined) searchParams.append("aceptado", String(params.aceptado));
  if (params.is_active !== undefined) searchParams.append("is_active", String(params.is_active));
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));
  if (params.ordering) searchParams.append("ordering", params.ordering);

  const url = searchParams.toString()
    ? `${CONSENTIMIENTOS_URL}?${searchParams}`
    : CONSENTIMIENTOS_URL;
  const response = await api.get<PaginatedResponse<ConsentimientoListItem>>(url);
  return response.data;
}

/**
 * Get a single consentimiento by ID
 */
export async function getConsentimiento(id: number): Promise<Consentimiento> {
  const response = await api.get<Consentimiento>(`${CONSENTIMIENTOS_URL}${id}/`);
  return response.data;
}

/**
 * Create a new consentimiento with scanned document upload
 */
export async function createConsentimiento(
  data: ConsentimientoCreateData
): Promise<Consentimiento> {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    }
  });
  const response = await api.post<Consentimiento>(CONSENTIMIENTOS_URL, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/**
 * Hard delete a consentimiento (permanent removal)
 */
export async function deleteConsentimiento(id: number): Promise<void> {
  await api.delete(`${CONSENTIMIENTOS_URL}${id}/`);
}

/**
 * Deactivate a consentimiento (soft delete)
 */
export async function deactivateConsentimiento(id: number): Promise<Consentimiento> {
  const response = await api.post<Consentimiento>(`${CONSENTIMIENTOS_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Activate a consentimiento
 */
export async function activateConsentimiento(id: number): Promise<Consentimiento> {
  const response = await api.post<Consentimiento>(`${CONSENTIMIENTOS_URL}${id}/activate/`);
  return response.data;
}

/**
 * Download .docx consent template for a given type.
 * Uses axios to include JWT auth, then triggers browser download.
 */
export async function descargarPlantilla(tipo: TipoConsentimiento): Promise<void> {
  const response = await api.get(`${CONSENTIMIENTOS_URL}descargar-plantilla/?tipo=${tipo}`, {
    responseType: "blob",
  });

  const contentDisposition = response.headers["content-disposition"] || "";
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : `plantilla-${tipo}.docx`;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Get required consent types for an exam code
 */
export async function getTiposRequeridos(
  codigoExamen: string
): Promise<{ codigo_examen: string; tipos_requeridos: TipoConsentimiento[] }> {
  const response = await api.get<{ codigo_examen: string; tipos_requeridos: TipoConsentimiento[] }>(
    `${CONSENTIMIENTOS_URL}tipos-requeridos/?codigo_examen=${encodeURIComponent(codigoExamen)}`
  );
  return response.data;
}

/**
 * Download a scanned consent document (PDF/image) via authenticated request.
 * Uses raw axios since documento_url is an absolute path (/media/...)
 * that shouldn't be prefixed with the API baseURL.
 */
export async function descargarDocumento(documentoUrl: string): Promise<string> {
  const token = localStorage.getItem("access_token");
  const response = await axios.get(documentoUrl, {
    responseType: "blob",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const contentType = response.headers["content-type"] || "application/octet-stream";
  const blob = new Blob([response.data], { type: contentType });
  return window.URL.createObjectURL(blob);
}
